import { useEffect, useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore } from '../store/useAuthStore';
import { authApi } from '../api/api';
import { LogIn, Mail, Lock, AlertCircle, CheckCircle, ShieldCheck } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { publicSupport } from '../content/publicSite';
import { friendlyErrorMessage } from '../utils/errorMessage';
import {
  trackClarityEvent,
  trackJourneyStart,
  trackJourneyStep,
  trackJourneyComplete,
  trackJourneyFailure,
} from '../lib/observability';

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});

type LoginForm = z.infer<typeof loginSchema>;

const isValidRole = (role: number): role is 1 | 2 | 3 | 4 | 5 | 6 => [1, 2, 3, 4, 5, 6].includes(role);

interface ApiError {
  response?: {
    status?: number;
    data?: {
      message?: string;
      errors?: string[];
    };
  };
}

// Shared input styling so Tenant / Email / Password are pixel-identical.
const fieldClass = (hasError: boolean) =>
  [
    'h-11 pl-10',
    hasError ? 'border-danger focus-visible:ring-danger' : '',
  ].join(' ');

export const Login = () => {
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);
  const bootstrap = useAuthStore((state) => state.bootstrap);
  const logout = useAuthStore((state) => state.logout);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const emailId = useId();
  const passwordId = useId();
  const emailErrId = useId();
  const passwordErrId = useId();

  const { register, handleSubmit, getValues, formState: { errors } } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  // Warm the Dashboard lazy chunk while the user signs in, so the first authenticated
  // paint doesn't stall on its import.
  useEffect(() => {
    import('./Dashboard').catch(() => {});
    trackJourneyStart('login');
    trackJourneyStep('login', 'form-viewed');
  }, []);

  const resolveRole = (raw: unknown): number => {
    if (raw === null || raw === undefined) return -1;

    if (Array.isArray(raw)) {
      for (const item of raw) {
        const parsed = resolveRole(item);
        if (isValidRole(parsed)) {
          return parsed;
        }
      }
      return -1;
    }

    const num = Number(raw);
    if (!isNaN(num) && isValidRole(num)) return num;
    const map: Record<string, number> = {
      'Admin': 1, 'Accountant': 2, 'Sales': 3, 'POSOperator': 4, 'Owner': 5, 'HRManager': 6,
      'admin': 1, 'accountant': 2, 'sales': 3, 'posoperator': 4, 'pos operator': 4, 'owner': 5, 'hrmanager': 6, 'hr manager': 6,
    };
    if (typeof raw === 'string') {
      const normalized = raw.trim();
      return map[normalized] ?? map[normalized.toLowerCase()] ?? -1;
    }
    return -1;
  };

  const onSubmit = async (data: LoginForm) => {
    setLoading(true);
    setError(null);
    trackClarityEvent('auth-login-started');
    trackJourneyStep('login', 'submitted');
    try {
      const response = await authApi.login(data);
      const { user, accessToken, refreshToken } = response.data.data;
      setAuth(user, accessToken, refreshToken);

      try {
        await bootstrap();
      } catch {
        logout();
        trackJourneyFailure('login', { failure_reason: 'bootstrap-failed' });
        setError('Unable to load your workspace. Please sign in again.');
        return;
      }

      const role = resolveRole(user?.role);
      if (!isValidRole(role)) {
        logout();
        trackJourneyFailure('login', { failure_reason: 'invalid-role' });
        setError('This account cannot access the tenant app. Please use the correct portal or contact support.');
        return;
      }

      trackClarityEvent('auth-login-succeeded');
      trackJourneyStep('login', 'authenticated');
      trackJourneyComplete('login', { first_login: Boolean(user?.isFirstLogin) });

      if (user?.isFirstLogin) {
        navigate('/change-password', { replace: true });
        return;
      }

      navigate('/dashboard', { replace: true });
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      const status = apiErr?.response?.status;
      trackClarityEvent('auth-login-failed', { login_failure_status: status ? String(status) : 'network' });
      trackJourneyFailure('login', { failure_reason: status === 401 ? 'bad-credentials' : 'request-failed' });
      if (status === 401) {
        setError(apiErr?.response?.data?.message || 'Invalid email or password.');
      } else {
        setError(friendlyErrorMessage(err, 'Login failed. Please check your credentials.'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    const email = getValues('email');
    if (!email) {
      setError('Enter your email above first, then click "Forgot password?".');
      return;
    }
    try {
      setForgotPasswordLoading(true);
      await authApi.requestPasswordReset(email);
      setForgotPasswordSent(true);
    } catch {
      // Always show success to prevent email enumeration
      setForgotPasswordSent(true);
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-12 font-sans antialiased">
      <div className="w-full max-w-[440px]">
        <div className="rounded-2xl border border-border bg-card p-8 shadow-sm sm:p-10">
          {/* Header */}
          <div className="mb-8 text-center">
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-md">
              <LogIn size={26} strokeWidth={2.5} />
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">Welcome to BizCore</h1>
            <p className="mt-1.5 text-sm text-muted-foreground">Sign in to manage your business</p>
          </div>

          <form
            data-testid="login-form"
            className="space-y-5"
            onSubmit={handleSubmit(onSubmit, () => trackClarityEvent('form-validation-failed', { form: 'login' }))}
            noValidate
          >
            {error && (
              <div data-testid="login-error" role="alert" aria-live="assertive" className="flex items-start gap-2.5 rounded-lg border border-danger/20 bg-danger-bg px-3.5 py-3 text-sm text-danger">
                <AlertCircle size={18} className="mt-0.5 shrink-0" />
                <p className="font-medium leading-snug">{error}</p>
              </div>
            )}

            {/* Email */}
            <div>
              <Label htmlFor={emailId} className="mb-1.5 block text-foreground">Email address</Label>
              <div className="relative">
                <Mail size={18} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id={emailId}
                  type="email"
                  data-testid="login-email"
                  {...register('email')}
                  className={fieldClass(!!errors.email)}
                  placeholder="name@company.com"
                  autoComplete="email"
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? emailErrId : undefined}
                />
              </div>
              {errors.email && <p id={emailErrId} className="mt-1.5 text-xs font-medium text-danger">{errors.email.message}</p>}
            </div>

            {/* Password */}
            <div>
              <Label htmlFor={passwordId} className="mb-1.5 block text-foreground">Password</Label>
              <div className="relative">
                <Lock size={18} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id={passwordId}
                  type="password"
                  data-testid="login-password"
                  data-clarity-mask="true"
                  {...register('password')}
                  className={fieldClass(!!errors.password)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  aria-invalid={!!errors.password}
                  aria-describedby={errors.password ? passwordErrId : undefined}
                />
              </div>
              {errors.password && <p id={passwordErrId} className="mt-1.5 text-xs font-medium text-danger">{errors.password.message}</p>}
              <div className="mt-1.5 text-right">
                {forgotPasswordSent ? (
                  <span className="flex items-center justify-end gap-1.5 text-xs font-medium text-success">
                    <CheckCircle size={14} />
                    If an account exists, a reset link has been sent.
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    disabled={forgotPasswordLoading}
                    className="rounded-sm text-xs font-medium text-primary transition-colors hover:text-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                  >
                    {forgotPasswordLoading ? 'Sending...' : 'Forgot password?'}
                  </button>
                )}
              </div>
            </div>

            {/* Submit */}
            <Button
              type="submit"
              data-testid="login-submit"
              loading={loading}
              className="mt-1 h-11 w-full text-sm font-semibold"
            >
              Sign in
            </Button>
          </form>

          <div className="mt-6 text-center space-y-3">
            <p className="text-sm text-muted-foreground">
              New to BizCore? <a href="/signup" className="text-primary hover:text-primary-hover font-medium">Start free trial</a>
            </p>
            <p className="text-xs text-muted-foreground">
              Need rollout help or waiting on activation? <a href={`mailto:${publicSupport.supportEmail}`} className="font-medium text-primary hover:text-primary-hover">Contact support</a>
            </p>
          </div>

          <a
            href="/security"
            className="mt-6 flex items-center justify-center gap-4 text-[10px] text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md"
            aria-label="Learn how BizCore secures your data"
          >
            <span className="flex items-center gap-1">
              <ShieldCheck className="h-3 w-3" />
              Encrypted
            </span>
            <span>•</span>
            <span>Isolated per tenant</span>
            <span>•</span>
            <span>Automated backups</span>
          </a>
        </div>
      </div>
    </div>
  );
};
